import { useLayoutEffect, useRef, useState } from "preact/hooks";
import { arrowUp, stop } from "../icons.ts";
import { Icon } from "./Icon.tsx";
import styles from "./Composer.module.css";

interface Props {
  placeholder: string;
  streaming: boolean;
  disabled: boolean;
  onSend(text: string): void;
  onStop(): void;
  inputRef: { current: HTMLTextAreaElement | null };
}

const MAX_LENGTH = 2000;

export function Composer({ placeholder, streaming, disabled, onSend, onStop, inputRef }: Props) {
  const [text, setText] = useState("");
  const local = useRef<HTMLTextAreaElement | null>(null);

  // Grows with the text up to five lines, then scrolls.
  useLayoutEffect(() => {
    const el = local.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 132)}px`;
  }, [text]);

  function submit() {
    const value = text.trim();
    if (!value || streaming || disabled) return;
    onSend(value);
    setText("");
  }

  return (
    <form
      class={styles.composer}
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <label class="visually-hidden" for="composer">
        Twoja wiadomość
      </label>
      <textarea
        id="composer"
        ref={(el) => {
          local.current = el;
          inputRef.current = el;
        }}
        class={styles.input}
        rows={1}
        value={text}
        maxLength={MAX_LENGTH}
        placeholder={placeholder}
        enterkeyhint="send"
        disabled={disabled}
        onInput={(e) => setText((e.currentTarget as HTMLTextAreaElement).value)}
        onKeyDown={(e) => {
          // Enter sends, Shift+Enter breaks the line; never while an IME is composing.
          if (e.key === "Enter" && !e.shiftKey && !e.isComposing) {
            e.preventDefault();
            submit();
          }
        }}
      />
      {streaming ? (
        <button type="button" class={styles.button} onClick={onStop} aria-label="Zatrzymaj odpowiedź" title="Zatrzymaj">
          <Icon svg={stop} />
        </button>
      ) : (
        <button type="submit" class={styles.button} disabled={!text.trim() || disabled} aria-label="Wyślij wiadomość" title="Wyślij">
          <Icon svg={arrowUp} />
        </button>
      )}
    </form>
  );
}
